import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FontsProvider } from "./context/FontsContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { Toaster } from "./components/ui/sonner";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Editor from "./pages/Editor";
import ShareView from "./pages/ShareView";
import AuthCallback from "./pages/AuthCallback";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import TrainingCenter from "./pages/TrainingCenter";
import StudioReplication from "./pages/StudioReplication";
import Auth from "./pages/Auth";
import AdminHomepage from "./pages/AdminHomepage";
import AiProStudio from "./pages/AiProStudio";
import DesignLibrary from "./pages/DesignLibrary";
import MyWorkspace from "./pages/MyWorkspace";
import LibraryAdmin from "./pages/LibraryAdmin";
import AdminBilling from "./pages/AdminBilling";
import WorkspaceBrands from "./pages/WorkspaceBrands";
import AccountSubscription from "./pages/AccountSubscription";

// Module-level dedup for GA4 page_view firing. Survives StrictMode remounts
// (which would otherwise double-fire the initial page_view in dev) and any
// unlikely fast remount races in production. Reset happens naturally when
// the module is re-loaded (fresh tab / full page reload).
let __lastGAPath = null;

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
        <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-blue-500 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Google Analytics 4 — manual page_view tracking for this SPA.
  // The gtag('config', ...) in index.html uses send_page_view: false so we
  // are the SINGLE source of truth for page_view events: this useEffect fires
  // exactly once on initial mount and once per client-side route change.
  //
  // Belt & suspenders: we prefer window.gtag() (available synchronously via
  // the inline snippet in index.html), but if for any reason it isn't ready
  // yet (async race, extension shimming, etc.) we push directly to the
  // window.dataLayer queue — gtag.js drains that queue when it finishes
  // loading, so the event still fires. No pageview is ever dropped.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = location.pathname + location.search;
    // Dedup: skip if we JUST fired a page_view for this exact path (protects
    // against StrictMode double-invocation in dev + any fast remount races).
    if (path === __lastGAPath) return;
    __lastGAPath = path;
    const params = {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    };
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", params);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(["event", "page_view", params]);
    }
  }, [location.pathname, location.search]);
  // Handle OAuth callback synchronously before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/signup" element={<Auth />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/admin/homepage" element={<ProtectedRoute><AdminHomepage /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><TrainingCenter /></ProtectedRoute>} />
      <Route path="/studio" element={<ProtectedRoute><StudioReplication /></ProtectedRoute>} />
      <Route path="/ai-pro-studio" element={<AiProStudio />} />
      <Route path="/library" element={<DesignLibrary />} />
      <Route path="/workspace" element={<ProtectedRoute><MyWorkspace /></ProtectedRoute>} />
      <Route path="/admin/library" element={<ProtectedRoute><LibraryAdmin /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute><AdminBilling /></ProtectedRoute>} />
      <Route path="/workspace/brands" element={<ProtectedRoute><WorkspaceBrands /></ProtectedRoute>} />
      <Route path="/account/subscription" element={<ProtectedRoute><AccountSubscription /></ProtectedRoute>} />
      <Route path="/editor/:id" element={<ProtectedRoute><Editor /></ProtectedRoute>} />
      <Route path="/share/:shareId" element={<ShareView />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <FontsProvider>
            <SubscriptionProvider>
              <AppRouter />
              <Toaster position="top-center" theme="dark" richColors />
            </SubscriptionProvider>
          </FontsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
